import { EditorContent, useEditor } from "@tiptap/react";

import { descriptionEditorExtensions } from "./descriptionEditor";

// RichTextEditor is the position description editor: paragraphs, bold,
// italic, bullet/numbered lists and links only — the same vocabulary the
// server's sanitizer keeps, so what you see is what survives the write. The
// value is an HTML string; the server re-sanitizes on every write. The
// extension config lives in ./descriptionEditor so the round-trip test uses
// exactly what renders here.
export function RichTextEditor({
  initialHTML,
  onChange
}: {
  initialHTML: string;
  onChange: (html: string, isEmpty: boolean) => void;
}) {
  const editor = useEditor({
    extensions: descriptionEditorExtensions,
    content: initialHTML,
    editorProps: {
      attributes: { "aria-label": "Description" }
    },
    // Toolbar active states (editor.isActive) must follow the selection.
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: current }) => onChange(current.getHTML(), current.isEmpty)
  });

  if (editor === null) {
    return null;
  }

  return (
    <div className="rich-text-editor">
      <div className="rich-text-editor__toolbar" role="toolbar" aria-label="Text formatting">
        <ToolbarButton
          label="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          label="Bullet list"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • List
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1. List
        </ToolbarButton>
        <ToolbarButton
          label="Remove link"
          disabled={!editor.isActive("link")}
          onClick={() => editor.chain().focus().unsetLink().run()}
        >
          Unlink
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

function ToolbarButton({
  label,
  active,
  disabled,
  onClick,
  children
}: {
  label: string;
  // Toggle buttons pass active (rendered as aria-pressed); the Unlink action
  // omits it. Unlink passes disabled when the cursor isn't in a link.
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className="btn btn--ghost btn--sm rich-text-editor__btn"
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      // Keep the editor selection: a default mousedown would blur it before
      // the click toggles the mark.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
